import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gift, Mail, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeadMagnet({ forceOpen = false, onClose }) {
  const [isOpen, setIsOpen] = useState(forceOpen);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Handle external forceOpen prop
  useEffect(() => {
    if (forceOpen) {
      setIsOpen(true);
      setIsSubmitted(false); // Reset submitted state when force opening
    }
  }, [forceOpen]);

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) onClose();
  };

  useEffect(() => {
    // Skip auto-popup if forceOpen is being used
    if (forceOpen) return;

    // Check if user has already submitted the lead magnet (permanent)
    const hasSubmittedLeadMagnet = localStorage.getItem('leadMagnetSubmitted');
    if (hasSubmittedLeadMagnet) {
      return; // Don't show if already submitted
    }

    // Check if already shown this session
    const hasSeenThisSession = sessionStorage.getItem('leadMagnetSeen');
    if (hasSeenThisSession) {
      return;
    }

    // Show on scroll to 50% of page
    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent > 50) {
        setIsOpen(true);
        sessionStorage.setItem('leadMagnetSeen', 'true');
        window.removeEventListener('scroll', handleScroll);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [forceOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/lead-magnet/subscribe`, {
        email: email.trim(),
        name: name.trim(),
        source: 'lead_magnet_popup'
      });
      
      setIsSubmitted(true);
      localStorage.setItem('leadMagnetSubmitted', 'true');
      toast.success('Check your email for the free guide!');
    } catch (error) {
      console.error('Lead magnet error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const benefits = [
    { id: "ben-1", text: "Why 60% of calls go unanswered (and what it costs you)" },
    { id: "ben-2", text: "The #1 mistake contractors make with voicemail" },
    { id: "ben-3", text: "How industry-trained AI knows your business better" },
    { id: "ben-4", text: "Real ROI numbers from service businesses" },
    { id: "ben-5", text: "Why regional knowledge makes all the difference" }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
            data-testid="lead-magnet-backdrop"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed top-[5vh] left-4 right-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-[calc(100%-2rem)] max-w-lg max-h-[90vh] bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl z-[101] overflow-y-auto"
            data-testid="lead-magnet-modal"
          >
            {/* Close Button - Highly Visible */}
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 p-2 bg-slate-800 hover:bg-red-500/80 border border-slate-600 hover:border-red-500 rounded-full transition-all z-10 group"
              data-testid="lead-magnet-close"
            >
              <X className="w-5 h-5 text-slate-300 group-hover:text-white" />
            </button>

            {!isSubmitted ? (
              <div className="p-6 sm:p-8">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
                    <Gift className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sky-400 text-sm font-medium">FREE GUIDE</p>
                    <h2 className="text-xl font-bold text-white">Stop Losing Leads</h2>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                  5 Ways Contractors{' '}
                  <span className="text-sky-400">Lose $2,000+ Weekly</span>{' '}
                  to Missed Calls
                </h3>
                <p className="text-slate-400 mb-6">
                  Get our free guide and learn how to capture every lead — even when you're on a job site.
                </p>

                {/* Benefits */}
                <div className="space-y-2 mb-6">
                  {benefits.map((benefit) => (
                    <div key={benefit.id} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-sky-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-300">{benefit.text}</span>
                    </div>
                  ))}
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-3">
                  <Input
                    type="text"
                    placeholder="Your name (optional)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="lead-magnet-name"
                  />
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="lead-magnet-email"
                  />
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-semibold py-6"
                    data-testid="lead-magnet-submit"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Get My Free Guide
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </form>

                <p className="text-xs text-slate-500 text-center mt-4">
                  No spam. Unsubscribe anytime. We respect your privacy.
                </p>
              </div>
            ) : (
              /* Success State */
              <div className="p-6 sm:p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Check Your Email!
                </h3>
                <p className="text-slate-400 mb-6">
                  We've sent your free guide to <span className="text-white">{email}</span>.
                  Check your inbox (and spam folder, just in case).
                </p>
                <Button
                  onClick={handleClose}
                  className="bg-slate-800 hover:bg-slate-700 text-white"
                >
                  Continue Browsing
                </Button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
